import { useState, useEffect, useRef } from "react";
import axios from "axios";
import usePhaseRedirect from "./usePhaseRedirect";

const API_URL = `${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetPoll`;
const POLL_MS = 3000;
const X_STEPS = 5;
const LABELS  = ["A", "B", "C", "D"];

const QUESTION = "How would you describe your organization's current stance toward Indonesia's digital infrastructure opportunity?";
const OPTIONS = [
  "All in — we're scaling aggressively",
  "Leaning in — actively growing our commitment",
  "Exploring — evaluating the right moment and model",
  "Watching closely — ready to move when conditions align",
];

/* ── Normalise any DB response shape into { votes: [num x4] } ── */
function normalise(raw) {
  if (!raw) return null;

  if (Array.isArray(raw)) {
    const map = {};
    raw.forEach(row => {
      const key   = (row.poll ?? row.option ?? row.answer ?? row.choice ?? "").toString().trim().toUpperCase();
      const count = 1;
      if (LABELS.includes(key)) map[key] = (map[key] ?? 0) + count;
    });
    return { votes: LABELS.map(l => map[l] ?? 0) };
  }

  if (typeof raw === "object") {
    return { votes: LABELS.map(l => parseInt(raw[l] ?? raw[l.toLowerCase()] ?? 0, 10)) };
  }

  return null;
}

function niceMax(_max) {
  return 100; // always 0–100%
}

/* ══════════════════════════════════════════════
   Sub-components
══════════════════════════════════════════════ */

function LoadingOverlay() {
  return (
    <div style={s.overlay}>
      <div style={s.spinnerRing}>
        <div style={s.spinnerDot} />
      </div>
      <div style={s.loadingText}>
        <p style={s.loadingTitle}>Fetching Poll Data</p>
        <p style={s.loadingSub}>Loading results...</p>
      </div>
    </div>
  );
}

function ErrorOverlay({ message }) {
  return (
    <div style={s.overlay}>
      <div style={s.errorBox}>
        <h2 style={s.errorTitle}>Unable to load poll</h2>
        <p style={s.errorMsg}>{message}</p>
      </div>
    </div>
  );
}

function HorizontalBar({ vote, total, isLead, index, animate, label, optionText }) {
  const pctOfTotal = total > 0 ? Math.round((vote / total) * 100) : 0;
  const widthPct   = pctOfTotal; // bar width IS the percentage

  const [currentWidth, setCurrentWidth] = useState(0);
  const [revealed, setRevealed]         = useState(false);

  useEffect(() => {
    const delay = animate ? 120 + index * 120 : 0;
    const t = setTimeout(() => {
      setCurrentWidth(widthPct);
      setRevealed(true);
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (revealed) setCurrentWidth(widthPct);
  }, [widthPct, revealed]);

  const fillStyle = {
    ...s.barFill,
    width: `${currentWidth}%`,
    background: isLead
      ? "linear-gradient(90deg, #c8940a 0%, #f7d75a 100%)"
      : "linear-gradient(90deg, #1d5fa0 0%, #5bb8f5 100%)",
    boxShadow: isLead
      ? "4px 0 20px rgba(232,200,74,0.3)"
      : "4px 0 16px rgba(61,148,210,0.2)",
  };

  return (
    <div style={s.barRow}>
      {/* Label badge */}
      <div style={{
        ...s.labelBadge,
        background: isLead ? "rgba(232,200,74,0.15)" : "rgba(255,255,255,0.06)",
        border: isLead ? "1px solid rgba(232,200,74,0.4)" : "1px solid rgba(255,255,255,0.1)",
        color: isLead ? "#e8c84a" : "rgba(255,255,255,0.55)",
        textShadow: isLead ? "0 0 16px rgba(232,200,74,0.5)" : "none",
      }}>
        {label}
      </div>

      {/* Option text + bar track */}
      <div style={s.barContent}>
        <div style={{
          ...s.optionText,
          color: isLead ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.55)",
          opacity: revealed ? 1 : 0,
          transform: revealed ? "translateX(0)" : "translateX(-8px)",
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}>
          {optionText}
        </div>

        <div style={s.barTrackRow}>
          <div style={s.barTrack}>
            <div style={fillStyle} />
            {/* Shimmer on lead */}
            {isLead && revealed && (
              <div style={s.shimmer} />
            )}
          </div>

          {/* Vote count + pct */}
          <div style={{
            ...s.barStats,
            opacity: revealed ? 1 : 0,
            transition: "opacity 0.4s ease 0.3s",
          }}>
            <span style={{
              ...s.barVoteCount,
              color: isLead ? "#e8c84a" : "rgba(255,255,255,0.6)",
            }}>
              {vote.toLocaleString()}
            </span>
            <span style={s.barPct}>{pctOfTotal}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════ */
export default function IftarPoll() {
  usePhaseRedirect();
  const [votes,   setVotes] = useState(null);
  const [error,   setError] = useState(null);
  const isFirstFetch        = useRef(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(API_URL);
        const raw = res.data;
        const data = normalise(raw);
        if (!data) throw new Error("Unexpected data shape");
        setVotes(data.votes);
        isFirstFetch.current = false;
      } catch (err) {
        console.error("[IftarPoll]", err);
        if (isFirstFetch.current) setError(err.message);
      }
    };

    fetchData();
    const id = setInterval(fetchData, POLL_MS);
    return () => clearInterval(id);
  }, []);

  const total    = votes ? votes.reduce((a, b) => a + b, 0) : 0;
  const leadIdx  = votes ? votes.indexOf(Math.max(...votes)) : -1;
  const animate  = isFirstFetch.current;

  return (
    <>
      <style>{`
        @keyframes iftarPollPulseDot {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.35; }
        }
        @keyframes iftarPollSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes iftarPollShimmer {
          0%   { left: -60%; }
          60%  { left: 120%; }
          100% { left: 120%; }
        }
      `}</style>

      <div style={s.page}>
        {!votes && !error && <LoadingOverlay />}
        {error             && <ErrorOverlay message={error} />}

        {/* Top Bar */}
        <header style={s.topbar}>
          <span style={s.topbarLeft}>
            <span style={s.dotLive}>●</span>
            LIVE POLL RESULTS
          </span>
          <span style={s.topbarRight}>Iftar Exchange 2026</span>
        </header>

        <main style={s.main}>

          {/* Title */}
          <div style={s.titleRow}>
            <h1 style={s.title}>Poll Results</h1>
            <span style={s.titleSub}>· IFTAR EXCHANGE 2026</span>
          </div>

          {/* Question */}
          <div style={s.questionBox}>
            <p style={s.questionText}>{QUESTION}</p>
          </div>

          {/* Horizontal Bars */}
          <div style={s.barsContainer}>
            {votes && votes.map((v, i) => (
              <HorizontalBar
                key={i}
                index={i}
                vote={v}
                total={total}

                isLead={i === leadIdx && v > 0}
                animate={animate}
                label={LABELS[i]}
                optionText={OPTIONS[i]}
              />
            ))}
          </div>

          {/* Total */}
          <div style={s.totalRow}>
            <span style={s.totalLabel}>TOTAL VOTES</span>
            <span style={{
              ...s.totalValue,
              color: total > 0 ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.35)",
            }}>
              {votes ? total.toLocaleString() : "—"}
            </span>
          </div>

        </main>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════
   Styles
══════════════════════════════════════════════ */
const s = {
  page: {
    height: "100vh",
    background: "linear-gradient(180deg, #07111f 0%, #0a1628 60%, #061018 100%)",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
    position: "relative",
  },

  /* Top Bar */
  topbar: {
    flexShrink: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 28px",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    background: "rgba(0,0,0,0.25)",
    height: 44,
  },
  topbarLeft: {
    fontSize: "clamp(10px, 1.2vw, 13px)",
    fontWeight: 600,
    letterSpacing: 3,
    color: "rgba(255,255,255,0.4)",
    textTransform: "uppercase",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  dotLive: {
    color: "#e8c84a",
    fontSize: 15,
    animation: "iftarPollPulseDot 2s ease-in-out infinite",
  },
  topbarRight: {
    fontSize: "clamp(10px, 1.2vw, 13px)",
    letterSpacing: 2,
    color: "rgba(255,255,255,0.3)",
    textTransform: "uppercase",
  },

  /* Main */
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "clamp(16px, 2.5vh, 28px) clamp(24px, 3.5vw, 52px)",
    gap: "clamp(12px, 1.8vh, 22px)",
    minHeight: 0,
  },

  /* Title */
  titleRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 16,
    borderBottom: "1px solid rgba(255,255,255,0.12)",
    paddingBottom: "clamp(10px, 1.2vh, 14px)",
    flexShrink: 0,
    flexWrap: "wrap",
    animation: "iftarPollSlideUp 0.6s cubic-bezier(0.2,0,0,1) both",
  },
  title: {
    fontSize: "clamp(34px, 3.8vw, 48px)",
    fontWeight: 700,
    color: "#e8c84a",
    letterSpacing: 0.3,
    lineHeight: 1,
    margin: 0,
  },
  titleSub: {
    fontSize: "clamp(13px, 1.4vw, 17px)",
    letterSpacing: 3,
    color: "rgba(255,255,255,0.55)",
    textTransform: "uppercase",
    fontWeight: 500,
  },

  /* Question */
  questionText: {
    margin: 0,
    fontSize: "clamp(23px, 2.3vw, 30px)",
    color: "rgba(255,255,255,0.82)",
    lineHeight: 1.5,
    fontStyle: "italic",
    letterSpacing: 0.2,
  },

  /* Bars Container */
  barsContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    gap: "clamp(10px, 1.5vh, 18px)",
    animation: "iftarPollSlideUp 0.6s cubic-bezier(0.2,0,0,1) 0.15s both",
    minHeight: 0,
  },

  /* Single bar row */
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: "clamp(10px, 1.2vw, 18px)",
    flex: 1,
    minHeight: 0,
  },

  labelBadge: {
    flexShrink: 0,
    width: "clamp(44px, 4vw, 60px)",
    height: "clamp(44px, 4vw, 60px)",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "clamp(20px, 2.2vw, 30px)",
    fontWeight: 800,
    letterSpacing: 1,
    transition: "all 0.4s ease",
  },

  barContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },

  optionText: {
    fontSize: "clamp(20px, 2vw, 25px)",
    fontWeight: 500,
    letterSpacing: 0.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  barTrackRow: {
    display: "flex",
    alignItems: "center",
    gap: "clamp(10px, 1vw, 16px)",
  },

  barTrack: {
    flex: 1,
    height: "clamp(32px, 4.5vh, 56px)",
    background: "rgba(255,255,255,0.04)",
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
    border: "1px solid rgba(255,255,255,0.06)",
  },

  barFill: {
    height: "100%",
    borderRadius: 6,
    transition: "width 1.1s cubic-bezier(0.16, 1, 0.3, 1)",
    position: "relative",
  },

  shimmer: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "40%",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",
    animation: "iftarPollShimmer 3s ease-in-out 1s infinite",
    pointerEvents: "none",
  },

  barStats: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 2,
    minWidth: "clamp(64px, 5.5vw, 84px)",
  },

  barVoteCount: {
    fontSize: "clamp(20px, 2.2vw, 30px)",
    fontWeight: 700,
    letterSpacing: 1,
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1,
  },

  barPct: {
    fontSize: "clamp(20px, 2vw, 25px)",
    color: "rgba(255,255,255,0.35)",
    letterSpacing: 1,
    fontWeight: 500,
  },

  /* Total */
  totalRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexShrink: 0,
    paddingTop: "clamp(6px, 1vh, 12px)",
    borderTop: "1px solid rgba(255,255,255,0.07)",
    animation: "iftarPollSlideUp 0.6s cubic-bezier(0.2,0,0,1) 0.3s both",
  },
  totalLabel: {
    fontSize: "clamp(15px, 1.5vw, 20px)",
    letterSpacing: 3,
    color: "rgba(255,255,255,0.4)",
    textTransform: "uppercase",
  },
  totalValue: {
    fontSize: "clamp(18px, 2vw, 26px)",
    fontWeight: 700,
    letterSpacing: 2,
    fontVariantNumeric: "tabular-nums",
    transition: "color 0.4s ease",
  },

  /* Overlays */
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 20,
    background: "#07111f",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
  },
  spinnerRing: {
    position: "relative",
    width: 64,
    height: 64,
    borderRadius: "50%",
    border: "3px solid rgba(232,200,74,0.12)",
  },
  spinnerDot: {
    position: "absolute",
    top: "50%", left: "50%",
    transform: "translate(-50%, -50%)",
    width: 7, height: 7,
    borderRadius: "50%",
    background: "#e8c84a",
    boxShadow: "0 0 10px rgba(232,200,74,0.8)",
  },
  loadingText: { textAlign: "center" },
  loadingTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 3,
    color: "#e8c84a",
    textTransform: "uppercase",
    marginBottom: 5,
  },
  loadingSub: {
    fontSize: 10,
    letterSpacing: 2,
    color: "rgba(255,255,255,0.35)",
    textTransform: "uppercase",
  },
  errorBox: {
    textAlign: "center",
    padding: 40,
    border: "1px solid rgba(220,80,80,0.3)",
    borderRadius: 12,
    background: "rgba(220,80,80,0.05)",
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 3,
    color: "rgba(220,80,80,0.8)",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  errorMsg: {
    fontSize: 12,
    color: "rgba(255,255,255,0.35)",
    letterSpacing: 1,
  },
};